import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router';
import { useSocketOperatorRooms } from './hooks/useSocketRooms';
import { useContextTenantScope } from './hooks/useContextTenantScope';
import { AppLayout } from './components/layout/AppLayout';
import { AppGate } from './components/AppGate';
import { tr } from './i18n/tr';
import { NoAccess } from './pages/NoAccess';
import {
  RootLanding,
  TenantRootRedirect,
  PagesRedirect,
  LegacyPageDashboardRedirect,
} from './components/RootLanding';

const TenantSelector = lazy(() =>
  import('./pages/TenantSelector').then((m) => ({ default: m.TenantSelector })),
);
const MyPersonas = lazy(() =>
  import('./pages/MyPersonas').then((m) => ({ default: m.MyPersonas })),
);
const MyCannedResponses = lazy(() =>
  import('./pages/MyCannedResponses').then((m) => ({ default: m.MyCannedResponses })),
);
const MyContacts = lazy(() =>
  import('./pages/MyContacts').then((m) => ({ default: m.MyContacts })),
);
const Inbox = lazy(() =>
  import('./pages/Inbox').then((m) => ({ default: m.Inbox })),
);
const Reports = lazy(() =>
  import('./pages/Reports').then((m) => ({ default: m.Reports })),
);

function MessagingRoutes() {
  // Auto-join operator + tenant socket rooms once authenticated & loaded.
  useSocketOperatorRooms();
  // A global admin's context workspace ends when its URL is left.
  useContextTenantScope();

  return (
    <AppLayout>
      <Suspense
        fallback={
          <div className="flex-1 flex items-center justify-center bg-surface-2">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-sm text-muted">{tr('common.loadingDots')}</p>
            </div>
          </div>
        }
      >
        <Routes>
          {/* Root: resume last tenant if available, else show selector */}
          <Route path="/" element={<RootLanding />} />
          {/* Explicit tenant picker URL stays available */}
          <Route path="/tenants" element={<TenantSelector />} />
          <Route path="/personas" element={<MyPersonas />} />
          <Route path="/canned-responses" element={<MyCannedResponses />} />
          <Route path="/contacts" element={<MyContacts />} />
          {/* Tenant root → /:slug/inbox (operator typed bare tenant URL) */}
          <Route path="/:tenantSlug" element={<TenantRootRedirect />} />
          {/* Page management surface — distinct from daily inbox workflow */}
          <Route path="/:tenantSlug/pages" element={<PagesRedirect />} />
          {/* Primary daily-driver workspace */}
          <Route path="/:tenantSlug/inbox" element={<Inbox />} />
          {/* Manager-facing analytics */}
          <Route path="/:tenantSlug/reports" element={<Reports />} />
          {/* Legacy per-page dashboard URL → unified inbox with page filter */}
          <Route
            path="/:tenantSlug/:channel/:pageId"
            element={<LegacyPageDashboardRedirect />}
          />
          <Route path="/no-access" element={<NoAccess />} />
          <Route path="*" element={<NoAccess />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

export default function AppRoutesMessaging() {
  return (
    <AppGate>
      <MessagingRoutes />
    </AppGate>
  );
}
